/**
 * The player's settings in memory: read from `play.profile` when signed in (cached in localStorage so
 * the board has them immediately), written back on change with a short debounce.
 */
import { create } from 'zustand';
import { getSupabase, playDb } from '../lib/supabase';
import { DEFAULT_PROFILE, type PlayProfile, parseProfile } from './schema';

const CACHE_KEY = 'runebound.play.settings';

function readCache(): PlayProfile {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { defaultTier?: unknown; settings?: unknown };
      return parseProfile({ default_tier: parsed.defaultTier, settings: parsed.settings });
    }
  } catch {
    // Unreadable cache (private mode): use the defaults.
  }
  return DEFAULT_PROFILE;
}

function writeCache(p: PlayProfile): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(p));
  } catch {
    // Storage full or blocked: settings still apply for this page view.
  }
}

interface SettingsState {
  profile: PlayProfile;
  /** `idle` until loaded from the database; `saving` while a write is in flight. */
  sync: 'idle' | 'loading' | 'ready' | 'saving' | 'error';
  load: (userId: string) => Promise<void>;
  update: (patch: (p: PlayProfile) => PlayProfile) => void;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let currentUser: string | null = null;

/** The settings store (one per tab). */
export const useSettings = create<SettingsState>()((set, get) => ({
  profile: readCache(),
  sync: 'idle',
  async load(userId) {
    currentUser = userId;
    if (!getSupabase()) return;
    set({ sync: 'loading' });
    const { data, error } = await playDb()
      .from('profile')
      .select('default_tier, settings')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      set({ sync: 'error' });
      return;
    }
    const profile = data ? parseProfile(data) : get().profile;
    writeCache(profile);
    set({ profile, sync: 'ready' });
  },
  update(patch) {
    const profile = patch(get().profile);
    writeCache(profile);
    set({ profile });
    if (!currentUser || !getSupabase()) return;
    if (saveTimer) clearTimeout(saveTimer);
    const user = currentUser;
    saveTimer = setTimeout(() => {
      set({ sync: 'saving' });
      void playDb()
        .from('profile')
        .upsert(
          { user_id: user, default_tier: profile.defaultTier, settings: profile.settings },
          { onConflict: 'user_id' },
        )
        .then(({ error }) => {
          set({ sync: error ? 'error' : 'ready' });
        });
    }, 500);
  },
}));

/** Forgets the signed-in user (sign-out). */
export function resetSettingsUser(): void {
  currentUser = null;
}
